import type { Contract, Market } from '../../domain/types'
import type { PredictionMarketAdapter } from './types'
import { addDays, todayIso } from '../synthetic'
import { liquidityScore } from '../../lib/math/predictionMarket'

function buildMarkets(): Market[] {
  return [
    {
      id: 'mkt-fed',
      title: 'Fed decision at the next meeting',
      category: 'Economics',
      source: 'mock',
      closeTime: undefined,
      resolutionDate: addDays(todayIso(), 28),
      mutuallyExclusive: true,
      correlationGroup: 'us-rates',
      contracts: [
        contract('ct-fed-cut', 'mkt-fed', 'Rate CUT', 38, 71000, 0.44, 'us-rates', 'Federal Reserve target-rate decision'),
        contract('ct-fed-hold', 'mkt-fed', 'Rate HOLD', 59, 120000, 0.54, 'us-rates', 'Federal Reserve target-rate decision'),
        contract('ct-fed-hike', 'mkt-fed', 'Rate HIKE', 5, 18000, 0.03, 'us-rates', 'Federal Reserve target-rate decision'),
      ],
    },
    {
      id: 'mkt-cpi',
      title: 'CPI year-over-year print this month',
      category: 'Economics',
      source: 'mock',
      resolutionDate: addDays(todayIso(), 12),
      mutuallyExclusive: true,
      correlationGroup: 'us-rates',
      contracts: [
        contract('ct-cpi-under', 'mkt-cpi', 'Below 3.0%', 46, 52000, 0.52, 'us-rates', 'BLS CPI release rounded to one decimal place'),
        contract('ct-cpi-over', 'mkt-cpi', '3.0% or higher', 54, 49000, 0.48, 'us-rates', 'BLS CPI release rounded to one decimal place'),
      ],
    },
    {
      id: 'mkt-launch',
      title: 'Will the flagship product ship before quarter end?',
      category: 'Tech',
      source: 'mock',
      closeTime: addDays(todayIso(), 38),
      resolutionDate: addDays(todayIso(), 45),
      mutuallyExclusive: true,
      correlationGroup: 'tech-launch',
      contracts: [
        contract('ct-launch-yes', 'mkt-launch', 'Ships on time', 27, 31000, 0.38, 'tech-launch', 'Company status page and public release notes'),
        contract('ct-launch-no', 'mkt-launch', 'Delayed', 73, 33000, 0.62, 'tech-launch', 'Company status page and public release notes'),
      ],
    },
  ]
}

function contract(
  id: string,
  marketId: string,
  title: string,
  yesPriceCents: number,
  volume: number,
  modelProb: number,
  group: string,
  criteria: string,
): Contract {
  const bidCents = Math.max(1, yesPriceCents - 2)
  const askCents = Math.min(99, yesPriceCents + 2)
  return {
    id,
    marketId,
    title,
    yesPriceCents,
    bidCents,
    askCents,
    lastCents: yesPriceCents,
    volume,
    liquidityScore: liquidityScore(volume, askCents - bidCents),
    modelProb,
    settlement: null,
    tags: [group],
    priceHistory: buildHistory(yesPriceCents, bidCents, askCents),
    resolutionSource: criteria.split(' and ')[0],
    resolutionCriteria: criteria,
  }
}

function buildHistory(last: number, bid: number, ask: number) {
  const offsets = [-4, -2, 1, -1, 2, 0]
  return offsets.map((offset, idx) => {
    const yesPriceCents = Math.max(1, Math.min(99, last + offset))
    return {
      time: addDays(todayIso(), idx - offsets.length + 1),
      yesPriceCents,
      bidCents: Math.max(1, Math.min(yesPriceCents, bid + offset)),
      askCents: Math.min(99, Math.max(yesPriceCents, ask + offset)),
    }
  })
}

export function createMockPredictionAdapter(): PredictionMarketAdapter {
  return {
    id: 'mock-prediction',
    name: 'Mock Prediction Markets (Kalshi-style)',
    requiresApiKey: false,
    description: 'Synthetic yes/no event contracts priced 1–99¢. Illustrative only — not live market data.',
    async fetchMarkets(): Promise<Market[]> {
      return buildMarkets()
    },
  }
}

export { buildMarkets as buildSampleMarkets }
